import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Body1,
  Button,
  Caption1,
  MessageBar,
  MessageBarBody,
  Subtitle1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkRegular, SettingsRegular } from '@fluentui/react-icons';

import { ToastContext } from '@shared/contexts';

import { APPROVAL_DECISION, CHANGE_STATUS } from '../models';
import { getChangeV2PlanPath } from '../helpers';
import {
  AffectedObjectsTable,
  AssessmentSection,
  AssessmentSummaryPanel,
  SectionNavigator,
  TemplateDetailsDrawer,
  getAssessmentNavItems,
} from '../components/assessment';
import { ApprovalDecisionDialog } from '../components/approval';
import { TApprovalInput, useChangeV2Store } from '../state/ChangeV2Store';
import { countCompleteSections, computeApproverRoles, getVisibleSections, isRecordReadOnly } from '../utils';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  headLeft: { display: 'flex', flexDirection: 'column', gap: '2px' },
  muted: { color: tokens.colorNeutralForeground3 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalXS, flexWrap: 'wrap' },
  layout: {
    display: 'grid',
    gridTemplateColumns: '260px minmax(0, 1fr)',
    gap: tokens.spacingHorizontalXL,
    alignItems: 'start',
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
  },
  sidebar: { position: 'sticky', top: tokens.spacingVerticalM, '@media (max-width: 900px)': { display: 'none' } },
  mobileNav: { display: 'none', '@media (max-width: 900px)': { display: 'block' } },
  content: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, minWidth: 0 },
});

const AssessmentPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { changeId } = useParams();
  const { show } = useContext(ToastContext);
  const {
    getChange,
    getAssessmentTemplate,
    ensureAssessment,
    submitAssessment,
    decideApproval,
    addTemplateCustomField,
    role,
    signedBaseline,
    currentUser,
  } = useChangeV2Store();

  const [activeId, setActiveId] = useState('');
  const [templateOpen, setTemplateOpen] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);

  const change = changeId ? getChange(changeId) : undefined;
  const inAssessment = change?.status === CHANGE_STATUS.IN_ASSESSMENT;

  // Instantiate the assessment lazily when a change reaches assessment without one.
  useEffect(() => {
    if (change && inAssessment && !change.assessment) ensureAssessment(change.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [change?.id, inAssessment, Boolean(change?.assessment)]);

  if (!change) return null;

  // States: not yet accepted, or already submitted/approved (read-only here).
  if (change.status === CHANGE_STATUS.DRAFT || change.status === CHANGE_STATUS.SUBMITTED) {
    return (
      <MessageBar intent="info">
        <MessageBarBody>The assessment becomes available once triage accepts the submitted request.</MessageBarBody>
      </MessageBar>
    );
  }

  const assessment = change.assessment;
  if (!assessment) {
    return (
      <MessageBar intent="info">
        <MessageBarBody>Preparing the assessment workspace…</MessageBarBody>
      </MessageBar>
    );
  }

  const readOnly = isRecordReadOnly(role, change, signedBaseline) || !inAssessment;
  const navItems = getAssessmentNavItems(change);
  const activeExists = navItems.some((item) => item.id === activeId);
  const currentId = activeExists ? activeId : (navItems[0]?.id ?? 'affected-objects');

  const total = getVisibleSections(assessment, change).length;
  const complete = countCompleteSections(assessment, change);
  const canApprove = !readOnly;

  const templateConfig = getAssessmentTemplate(assessment.templateId);
  const approverRoles = assessment.submission?.approverRoles ?? computeApproverRoles(assessment, change);
  const approvalSections = getVisibleSections(assessment, change).map((section) => ({
    id: section.id,
    title: section.title,
  }));

  const handleApproval = (input: TApprovalInput) => {
    const submission = assessment.submission ?? submitAssessment(change.id);
    if (!submission) {
      show('error', 'The assessment is unavailable. Please reopen the change.');
      return;
    }
    decideApproval(change.id, input);
    setApprovalOpen(false);
    show('success', 'Assessment approved. A signed baseline and draft planning items were created.');
    navigate(getChangeV2PlanPath(change.id));
  };

  const activeSection = getVisibleSections(assessment, change).find((section) => section.id === currentId);
  const moveToNextSection = () => {
    const currentIndex = navItems.findIndex((item) => item.id === currentId);
    const nextItem = navItems[currentIndex + 1];
    if (nextItem) setActiveId(nextItem.id);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headLeft}>
          <Subtitle1>Assessment</Subtitle1>
          <Caption1 className={classes.muted}>
            {complete} of {total} sections reviewed · All sections and fields are optional
          </Caption1>
        </div>
        <div className={classes.actions}>
          <Button appearance="subtle" size="small" icon={<SettingsRegular />} onClick={() => setTemplateOpen(true)}>
            View template details
          </Button>
          <Button
            appearance="primary"
            size="small"
            icon={<CheckmarkRegular />}
            disabled={!canApprove}
            onClick={() => setApprovalOpen(true)}
          >
            Approve
          </Button>
        </div>
      </div>

      {readOnly && !inAssessment ? (
        <MessageBar intent="info">
          <MessageBarBody>
            This assessment is approved and read-only.{' '}
            <Button appearance="transparent" size="small" onClick={() => navigate(getChangeV2PlanPath(change.id))}>
              Open planning
            </Button>
          </MessageBarBody>
        </MessageBar>
      ) : null}

      <div className={classes.mobileNav}>
        <SectionNavigator change={change} activeId={currentId} onSelect={setActiveId} variant="dropdown" />
      </div>

      <div className={classes.layout}>
        <div className={classes.sidebar}>
          <SectionNavigator change={change} activeId={currentId} onSelect={setActiveId} />
        </div>

        <div className={classes.content}>
          {currentId === 'affected-objects' ? <AffectedObjectsTable change={change} readOnly={readOnly} /> : null}
          {currentId === 'summary' ? <AssessmentSummaryPanel change={change} /> : null}
          {activeSection ? (
            <AssessmentSection
              change={change}
              section={activeSection}
              readOnly={readOnly}
              onCompleted={moveToNextSection}
            />
          ) : null}

          {currentId !== 'affected-objects' && currentId !== 'summary' && !activeSection ? (
            <Body1 className={classes.muted}>This section is not currently shown for this change.</Body1>
          ) : null}
        </div>
      </div>

      <TemplateDetailsDrawer
        template={templateConfig}
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        admin
        onAddCustomField={(input) => {
          addTemplateCustomField(input);
          show('success', 'Custom field added to the template. It applies to newly created changes.');
        }}
      />
      <ApprovalDecisionDialog
        decision={approvalOpen ? APPROVAL_DECISION.APPROVE : undefined}
        currentUser={currentUser}
        approverRoles={approverRoles}
        sections={approvalSections}
        onClose={() => setApprovalOpen(false)}
        onConfirm={handleApproval}
      />
    </div>
  );
};

export default AssessmentPage;
